import { useState, type FormEvent } from 'react'
import { createOrganisation } from '../api/endpoints'
import type { Organisation, User } from '../api/types'
import { Centered } from '../components/Centered'

/**
 * Which organisation you are running today.
 *
 * Only reached when the answer is not obvious: one organisation goes straight through, because a
 * choice with one option is not a choice. It also carries the empty case, which is what a fresh
 * instance's administrator sees and needs a way out of.
 */
export function OrganisationPicker({
  user,
  organisations,
  onPick,
  onSignOut,
  onCreated,
}: {
  user: User
  organisations: Organisation[]
  onPick: (organisation: Organisation) => void
  onSignOut: () => void
  onCreated: () => void
}) {
  const isAdmin = user.authorities?.includes('ROLE_ADMIN') ?? false
  const [creating, setCreating] = useState(organisations.length === 0 && isAdmin)

  if (creating) {
    return <CreateOrganisation onDone={onCreated} onCancel={() => setCreating(false)} />
  }

  return (
    <Centered>
      <div className="card stack" style={{ width: 460 }}>
        <div className="spread">
          <h1>Your organisations</h1>
          <span className="tertiary">{user.callsign}</span>
        </div>

        {organisations.length === 0 ? (
          <>
            <div className="banner info">
              You are not a member of any organisation yet.
              {isAdmin
                ? ' As this instance’s administrator you can create one.'
                : ' Ask whoever administers this server to add you to theirs.'}
            </div>
            {!isAdmin && (
              <p className="tertiary">
                Only the server’s administrator can create an organisation. That is what stops any
                player making one and putting on events.
              </p>
            )}
          </>
        ) : (
          <div className="stack">
            {organisations.map((organisation) => (
              <button
                key={organisation.id}
                className="secondary"
                style={{ height: 'auto', padding: 12, textAlign: 'left' }}
                onClick={() => onPick(organisation)}
              >
                <div className="spread">
                  <div>
                    <div style={{ fontWeight: 600 }}>{organisation.name}</div>
                    <div className="tertiary">
                      {organisation.missionCount} event
                      {organisation.missionCount === 1 ? '' : 's'} ·{' '}
                      {organisation.memberCount} member
                      {organisation.memberCount === 1 ? '' : 's'}
                    </div>
                  </div>
                  <span className="pill neutral">{organisation.myRole ?? 'ADMIN'}</span>
                </div>
              </button>
            ))}
          </div>
        )}

        <div className="row">
          {isAdmin && (
            <button onClick={() => setCreating(true)}>New organisation</button>
          )}
          <button className="ghost" onClick={onSignOut}>
            Sign out
          </button>
        </div>
      </div>
    </Centered>
  )
}

function CreateOrganisation({
  onDone,
  onCancel,
}: {
  onDone: () => void
  onCancel: () => void
}) {
  const [name, setName] = useState('')
  const [contact, setContact] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)

    // No owner given, so the administrator creating it becomes the owner. On a club's own instance
    // those are the same person, which is the common case and not worth a second question.
    const result = await createOrganisation({ name: name.trim(), contact: contact.trim() || undefined })
    setBusy(false)

    if (!result.ok) {
      setError(result.error.message)
      return
    }
    onDone()
  }

  return (
    <Centered>
      <form className="card stack" onSubmit={submit} style={{ width: 420 }}>
        <h1>New organisation</h1>
        <p className="muted" style={{ margin: 0 }}>
          The club or league that puts on events. You will be its owner.
        </p>

        <div className="field">
          <label htmlFor="name">Name</label>
          <input
            id="name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Lombardia Milsim League"
            autoFocus
          />
        </div>

        <div className="field">
          <label htmlFor="contact">Contact (optional)</label>
          <input
            id="contact"
            value={contact}
            onChange={(event) => setContact(event.target.value)}
            placeholder="A website, an email, a number for the day"
          />
        </div>

        {error && <div className="banner danger">{error}</div>}

        <div className="row">
          <button type="submit" disabled={busy || !name.trim()}>
            {busy ? 'Creating…' : 'Create'}
          </button>
          <button type="button" className="ghost" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </form>
    </Centered>
  )
}
